import dayjs from "dayjs";
import { toEndOfDay, toStartOfDay } from "../shared/formatting";
import { reportTypes } from "./reportParameters";
import { fixedDateRanges } from "../shared/form/fixedDateRanges";

//
const getSearch = (location) => (location && location.search) || "";

const parseBool = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().toLowerCase();
  if (s === "true") return true;
  if (s === "false") return false;
  return null;
};

const mapTypeParamToReportType = (v) => {
  if (!v) return null;
  const s = String(v).trim().toLowerCase();
  if (s === "detail") return reportTypes.DETAIL;
  if (s === "summary") return reportTypes.SUMMARY;
  if (s === "summary_condensed") return reportTypes.SUMMARY_CONDENSED;
  if (s === "sales") return reportTypes.SALES_SUMMARY;
  if (s === "comparison") return reportTypes.COMPARISON;
  if (s === "comparison_condensed") return reportTypes.COMPARISON_CONDENSED;
  return null;
};

const parseStrictDate = (v) => {
  if (!v) return null;
  const d = dayjs(v, "YYYY-MM-DD", true); // strict
  return d.isValid() ? d : null;
};

//
const parseAgenciesRaw = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const l = s.toLowerCase();
  if (l === "all") return { kind: "all" };
  if (l === "home") return { kind: "home" };
  if (/^\d+(,\d+)*$/.test(s)) {
    const ids = s
      .split(",")
      .slice(0, 200)
      .map((n) => n.trim()); // keep as strings (preserve leading zeros)
    return { kind: "list", ids };
  }
  return null;
};

const resolveAgencyKeys = (params, list, homeAgencyKey) => {
  if (!params) return undefined;
  if (!Array.isArray(list)) return undefined;

  // All agencies
  if (params.kind === "all") {
    const allIds = list
      .map((a) => (a ? a.Id : null))
      .filter((id) => id != null);
    return allIds.length ? allIds : undefined;
  }

  // Home agency
  if (params.kind === "home") {
    return homeAgencyKey != null ? [homeAgencyKey] : undefined;
  }

  // List of agency numbers
  if (
    params.kind === "list" &&
    Array.isArray(params.ids) &&
    params.ids.length > 0
  ) {
    const numberToId = new Map(list.map((a) => [String(a.Number), a.Id]));
    const newList = params.ids
      .map((num) => numberToId.get(String(num)))
      .filter((id) => id != null);

    return newList.length ? newList : undefined;
  }

  return undefined;
};

//
export const applyProductionReportUrlDefaults = (initialValues, opts = {}) => {
  const { agenciesList, homeAgencyKey, location } = opts;
  const params = new URLSearchParams(getSearch(location));

  const merged = { ...initialValues };

  //
  const typeOv = mapTypeParamToReportType(params.get("type"));
  if (typeOv) merged.ReportType = typeOv;

  //
  const canc = parseBool(params.get("include_cancellations"));
  if (canc !== null) merged.IncludeCancellations = canc;

  const includeFleet = parseBool(params.get("include_fleet"));
  if (includeFleet !== null) merged.IncludeFleetGarage = includeFleet;

  const includeNonFleet = parseBool(params.get("include_non_fleet"));
  if (includeNonFleet !== null)
    merged.IncludeNonFleetNonGarage = includeNonFleet;

  //
  if (!merged.IncludeFleetGarage && !merged.IncludeNonFleetNonGarage) {
    merged.IncludeNonFleetNonGarage = true;
  }

  const from = parseStrictDate(params.get("from"));
  const to = parseStrictDate(params.get("to"));

  // Only apply if BOTH are provided and valid
  if (from && to) {
    const newFrom = toStartOfDay(from);
    const newTo = toEndOfDay(to);
    if (
      !dayjs(newFrom).isAfter(dayjs(newTo)) &&
      // range <= 1 year (same year diff)
      dayjs(newTo).diff(dayjs(newFrom), "year") === 0
    ) {
      merged.TransactionDate = [newFrom, newTo];
      merged.FixedDateRange = fixedDateRanges.NONE;
    }
  }

  const agenciesRaw = parseAgenciesRaw(params.get("agencies"));
  const agencyKeys = resolveAgencyKeys(
    agenciesRaw,
    agenciesList,
    homeAgencyKey
  );
  if (agencyKeys && agencyKeys.length) merged.AgencyKeys = agencyKeys;

  return merged;
};
